import { createElement, useMemo } from 'react';
import { Box, Tooltip } from '@mui/joy';
import { SortingMode } from 'ka-table';
import { upperFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { type ColumnProps, type HeadCellProps } from '@/shared/hooks/table/types';
import { type Obj } from '~/common/types';
import { type SortSettings } from '~/common/types/sort';

import useTruncated from '@/shared/hooks/useTruncated';

import { ArrowDownIcon, ArrowUpIcon } from '@/shared/icons/Icons';

type Props = Obj &
  HeadCellProps & {
    type?: string | null;
    column: ColumnProps;
    sortSettings: SortSettings[];
    setSortSettings: React.Dispatch<React.SetStateAction<SortSettings[]>>;
  } & React.Attributes;

const SortableHeadCell: React.FC<Props> = (props) => {
  const { type = null, column, sortSettings, setSortSettings, sortingMode } = props;

  const { childRef, containerRef, isTruncated } = useTruncated();
  const { t } = useTranslation();

  const sortSetting = useMemo(
    () => sortSettings.find((setting) => setting.sortColumn === upperFirst(column.key)) ?? null,
    [sortSettings, column.key],
  );

  const label = useMemo(() => {
    const { title, key } = column;

    if (!type && !title) return '';

    return title ? t(title) : t(`_${type}.columns.${key}`);
  }, [column, t, type]);

  if (column.customHeaderComponent) {
    return createElement(column.customHeaderComponent, props);
  }

  const handleClick = () => {
    const columnName = upperFirst(column.key);

    if (sortSetting) {
      const filteredSortSettings =
        sortSettings.filter(
          (settings) => settings.sortColumn !== columnName && settings.sortColumn !== '',
        ) ?? [];

      // deselect filter
      if (!sortSetting.defaultSortOrder) {
        setSortSettings(filteredSortSettings);
        return;
      }

      if (sortingMode === SortingMode.Single) {
        setSortSettings([
          {
            defaultSortOrder: false,
            sortColumn: columnName,
          },
        ]);
        return;
      }

      setSortSettings([
        ...filteredSortSettings,
        {
          defaultSortOrder: false,
          sortColumn: columnName,
        },
      ]);
      return;
    }

    if (sortingMode === SortingMode.Single) {
      setSortSettings([
        {
          defaultSortOrder: true,
          sortColumn: columnName,
        },
      ]);
      return;
    }

    setSortSettings((prevState) => [
      ...prevState,
      {
        defaultSortOrder: true,
        sortColumn: columnName,
      },
    ]);
  };
  const sortIcon = sortSetting ? (
    sortSetting.defaultSortOrder ? (
      <ArrowDownIcon size="sm" />
    ) : (
      <ArrowUpIcon size="sm" />
    )
  ) : null;

  return (
    <Box ref={containerRef} onClick={handleClick} sx={{ position: 'relative' }}>
      <div className="inline-block" ref={childRef}>
        {isTruncated ? (
          <Tooltip className="whitespace-nowrap" placement="top" title={label}>
            <div className="inline-block whitespace-nowrap">{label}</div>
          </Tooltip>
        ) : (
          <div className="inline-block whitespace-nowrap">
            {label}
            {sortIcon && <span className="pl-2">{sortIcon}</span>}
          </div>
        )}
      </div>
      {isTruncated && sortIcon && (
        <Box
          component="span"
          sx={{
            bgcolor: 'background.surface',
            pl: 1,
            position: 'absolute',
            right: '-20px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          {sortIcon}
        </Box>
      )}
    </Box>
  );
};

export default { SortableHeadCell };
